/**
 * aside.js - Pinia store for eSign Portal sidebar/layout state
 *
 * Centralized menu configuration and active page tracking.
 * Pattern follows Admin project's aside.js store.
 */
import { defineStore } from 'pinia';
import { logout as sharedLogout } from '../services/authActions.js';

export const useAsideStore = defineStore('aside', {
    state: () => ({
        // Layout state
        drawerOpen: true,
        showHeader: true,

        // Current page tracking (set by each view on mount)
        currentViewKey: '',

        // User info (populated from _pageConfig)
        tenantName: '',
        userName: '',
        userEmail: '',
        currentUserId: '',

        // Sandbox-tenant-mode.md v8 rule 21: true when the resolved tenant has
        // ApolloTenant.IsSandbox = 1. AppLayout renders a persistent red
        // SANDBOX MODE banner across the entire UI when this is true.
        // hasSandboxSibling is the live-tenant side (LinkedSandboxTenantId
        // IS NOT NULL); used by Developer Settings to pick button text.
        isSandbox: false,
        hasSandboxSibling: false,

        // EV-1 + UI-1: ApplicationUser.EmailConfirmed. False shows the
        // "Please verify your email" banner above the toolbar. Defaults
        // to true so non-authenticated / loading states don't briefly
        // flash a misleading banner.
        emailConfirmed: true,

        // Primary daily-work items. Rendered under the "Navigation" section label for now;
        // step 2 of the restructure drops the label so the top section reads unlabeled.
        navigationLinks: [
            { icon: 'dashboard', text: 'Dashboard', viewkey: 'dashboard_index', url: '/Dashboard' },
            { icon: 'inventory_2', text: 'Packages', viewkey: 'packages_index', url: '/Packages', requiresPermission: 'canViewPackages' },
            { icon: 'description', text: 'Templates', viewkey: 'templates_index', url: '/Templates', requiresPermission: 'canViewTemplates' },
            // Bulk Sends - viewkey prefix `bulksends_` covers _index, _new,
            // _details (set by each Vue view at mount). canViewPackages is
            // the access gate to match the proxy controller's role check.
            // requiresFeature is metadata only - AppSidebar.linkVisible()
            // intentionally ignores it so gated links stay visible and the
            // page itself handles the upgrade banner + per-action upgrade
            // prompt (UX principle: let the user see what they would get).
            { icon: 'forward_to_inbox', text: 'Bulk Sends', viewkey: 'bulksends_index', url: '/BulkSends', requiresPermission: 'canViewPackages', requiresFeature: 'allowBulkSend' },
            { icon: 'groups', text: 'Signing Groups', viewkey: 'groups_index', url: '/Groups', requiresPermission: 'canViewGroups', requiresFeature: 'allowSigningGroups' }
        ],

        // Organization - who works in the workspace and how they log in. Rendered under the
        // "Organization" section label in AppSidebar (the underlying array keeps the historical
        // name `settingsLinks` so we don't have to churn more files than necessary).
        //
        // `hiddenInSandbox: true` on every row in this section: the sandbox is intended for
        // API / integration testing, not real-world team / branding / SSO setup. Server-side
        // controllers don't block direct-URL navigation (the pages still load against the
        // sandbox tenant's data), but the nav rows are hidden so users don't accidentally edit
        // sandbox-isolated team/branding/SSO state thinking they're touching live config.
        settingsLinks: [
            { icon: 'people',    text: 'Team',           viewkey: 'settings_users',           url: '/Settings/Users',         requiresPermission: 'canManageTeam', hiddenInSandbox: true },
            { icon: 'palette',   text: 'Branding',       viewkey: 'settings_branding',        url: '/Settings/Branding',      requiresPermission: 'canManageTeam', requiresFeature: 'allowBranding', hiddenInSandbox: true },
            { icon: 'security',  text: 'Identity & SSO', viewkey: 'settings_identity',        url: '/Settings/Identity',      requiresPermission: 'canManageTeam', requiresFeature: 'allowSsoScim', hiddenInSandbox: true }
        ],

        // Billing & Payments - all money-in-and-out surfaces, gated on canManageBilling.
        // "Signer Payments" is the money-received side (packages with signer-paid fees, was
        // just "Payments"). "Bank Connection" wraps the Stripe Connect onboarding wizard under
        // a brand-neutral name (was "Payment Collection"). The underlying array keeps the
        // historical name `enterpriseLinks` to minimise file churn during the restructure.
        enterpriseLinks: [
            // Billing is hiddenInSandbox: every flow it exposes (Add Payment Method, Auto-Refill)
            // is gated off in sandbox - sandbox gets unlimited free auth and never bills - so the
            // row would be misleading. Server-side: SettingsController.Billing picks up the
            // [BlockedInSandbox] attribute and redirects direct-URL hits to /Dashboard.
            //
            // Subscription stays visible: it shows the sandbox plan summary (Plan Cost Free, ∞
            // credits, usage this period) which is useful for devs to see. All write actions on
            // that page (Change Plan, Buy Credits, Cancel, Reactivate) are already gated.
            //
            // Signer Payments + Bank Connection stay visible: they cover the Stripe Connect
            // merchant-onboarding flow, which devs DO test in sandbox via test mode.
            { icon: 'credit_card',             text: 'Billing',         viewkey: 'settings_billing',      url: '/Settings/Billing',         requiresPermission: 'canManageBilling', hiddenInSandbox: true },
            { icon: 'account_balance_wallet',  text: 'Subscription',    viewkey: 'settings_subscription', url: '/Settings/Subscription',    requiresPermission: 'canManageBilling' },
            { icon: 'receipt_long',            text: 'Signer Payments', viewkey: 'payments_index',        url: '/Payments',                 requiresPermission: 'canManageBilling' },
            { icon: 'account_balance',         text: 'Bank Connection', viewkey: 'settings_payment',      url: '/Settings/PaymentSettings', requiresPermission: 'canManageBilling', requiresFeature: 'allowPaymentCollection' }
        ],

        // Developers - live integration config. "Developer Settings" is the canonical
        // hub introduced in Phase 4 of sandbox-tenant-mode.md v8 (rule 21 + §6 Phase 4):
        // surfaces sandbox-tenant provisioning, quick links to API Keys / Webhooks /
        // Integrations / Embedded Signing, and (future) MCP install instructions +
        // connector links. The legacy individual rows stay so users keep their existing
        // muscle memory; the hub is additive.
        //
        // API Keys are gated strictly on Tenant Admin - the server-side controller is
        // [Authorize(Roles="Tenant Admin")] and we want the nav link to match so non-admins
        // don't see a row they can't use. Webhooks remain on canManageTeam so team admins can
        // wire integrations without needing the owner's credentials.
        developerLinks: [
            // Labelled "Test Settings" customer-facing; URL/viewkey/route still
            // /Settings/DeveloperSettings so we don't break bookmarks or backlinks.
            // Gated on canAccessDeveloperSettings (Tenant Admin OR Developer role) -
            // matches the controller's [Authorize(Roles="Tenant Admin,Developer")].
            { icon: 'science',      text: 'Test Settings',      viewkey: 'settings_developersettings', url: '/Settings/DeveloperSettings', requiresPermission: 'canAccessDeveloperSettings' },
            // Integrations is the production-CRM connector discovery (HubSpot today; more landing
            // soon). Hidden in sandbox because the connectors here hook into real-world accounts -
            // sandbox uses Test Settings + Connected Apps for OAuth testing instead. The controller
            // is also gated with [BlockedInSandbox] so a direct-URL hit redirects to /Dashboard.
            { icon: 'hub',          text: 'Integrations',       viewkey: 'settings_integrations',      url: '/Settings/Integrations', hiddenInSandbox: true },
            { icon: 'api',          text: 'API Keys',           viewkey: 'settings_api',               url: '/Settings/Api',          requiresTenantAdmin: true, requiresFeature: 'allowApiAccess' },
            { icon: 'webhook',      text: 'Webhooks',           viewkey: 'settings_webhooks',          url: '/Settings/Webhooks',     requiresPermission: 'canManageTeam', requiresFeature: 'allowWebhooks' },
            // Embedded Signing - frame-ancestors allowlist for tenants embedding the ceremony in an iframe.
            // Tenant Admin only because changing this directly affects what origins can frame the signing flow.
            { icon: 'open_in_browser', text: 'Embedded Signing', viewkey: 'settings_embedding',         url: '/Settings/Embedding',    requiresTenantAdmin: true, requiresFeature: 'allowEmbeddedSigning' },
            // Connected Apps moved here from Organization (2026-05-19). It's the per-user OAuth
            // grant management page (each user sees + revokes their OWN third-party authorizations),
            // which fits the integration/developer-tooling story better than the team-management
            // story. No permission gate - every authenticated user gets it.
            { icon: 'extension',    text: 'Connected Apps',     viewkey: 'settings_connected_apps',    url: '/Settings/ConnectedApps' }
        ],

        // Single entry point to the canonical docs mini-site at /Docs/IntegrationGuides.
        // All guide topics + API Reference live inside that page (driven by the GuideSidebar
        // inside the content), so the Dashboard aside doesn't need to mirror 15 topic links.
        // viewkey ends in `_index` so the aside's isActive() prefix-matches any sub-page
        // (e.g. `docs_guides_overview`) back to this single row.
        documentationLinks: [
            { icon: 'menu_book', text: 'Documentation', viewkey: 'docs_guides_index', url: '/Docs/IntegrationGuides' }
        ]
    }),

    actions: {
        /**
         * Initialize store from page config
         * @param {Object} config - window._pageConfig object
         */
        initFromConfig(config) {
            if (!config) return;
            this.tenantName = config.tenantName || 'eSign Launchpad';
            this.userName = config.userName || 'User';
            this.userEmail = config.userEmail || '';
            this.currentUserId = config.currentUserId || '';
            this.isSandbox = !!config.isSandbox;
            this.hasSandboxSibling = !!config.hasSandboxSibling;
            // EV-1 + UI-1: default to true when the config omits the
            // flag so we never display the verify banner on a state
            // where we genuinely don't know the verification status.
            this.emailConfirmed = config.emailConfirmed === undefined ? true : !!config.emailConfirmed;
        },

        /**
         * Mark the user's email as confirmed in the store. Called after
         * a successful Resend + click + reload, or any other in-session
         * event that flips EmailConfirmed=true. Optimistic update so
         * the banner disappears immediately without a full reload.
         */
        markEmailConfirmed() {
            this.emailConfirmed = true;
        },

        /**
         * Set the current view key (called by each page on mount)
         * @param {string} viewkey - The current page's viewkey
         */
        setCurrentView(viewkey) {
            this.currentViewKey = viewkey;
        },

        /**
         * Check if a menu item is active. Matches sub-pages: "packages_details" highlights
         * "packages_index" so nested pages keep the parent row lit up.
         * @param {string} viewkey - The menu item's viewkey
         * @returns {boolean}
         */
        isActive(viewkey) {
            if (this.currentViewKey === viewkey) return true;
            if (viewkey.endsWith('_index')) {
                var prefix = viewkey.replace(/_index$/, '_');
                return this.currentViewKey.startsWith(prefix);
            }
            return false;
        },

        /**
         * Check if a parent group should be expanded (any child is active).
         * @param {Object} link - The parent link with children
         * @returns {boolean}
         */
        isGroupExpanded(link) {
            if (!link.children) return false;
            return link.children.some(child => this.isActive(child.viewkey));
        },

        /**
         * Toggle the drawer open/closed
         */
        toggleDrawer() {
            this.drawerOpen = !this.drawerOpen;
        },

        /**
         * Logout the user. Delegates to the shared authActions helper so the
         * Loading overlay, CSRF handling, and form-submit redirect semantics
         * stay in one place (and SAML SLO actually completes).
         */
        logout() {
            sharedLogout();
        }
    }
});
